import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Alert, IconButton, CircularProgress, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Divider
} from '@mui/material';
import { Home, MoveToInbox, Inventory } from '@mui/icons-material';
import axios from 'axios';
import { fmt, sym } from '../utils/currency';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmtD = (d) => d ? new Date(d).toLocaleDateString() : '';

const PurchaseReceiptList = () => {
  const navigate = useNavigate();
  const [receipts, setReceipts] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [view,     setView]     = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/payables/receipts`, { headers: hdrs() })
      .then(r => setReceipts(r.data))
      .catch(err => setError(err.response?.data?.error || 'Failed to load receipts'))
      .finally(() => setLoading(false));
  }, []);

  const qtyOf = (r) => (r.lines || []).reduce((s, l) => s + (l.qty || 0), 0);
  const valOf = (r) => (r.lines || []).reduce((s, l) => s + (l.amount || 0), 0);

  if (loading) return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4, textAlign: 'center' }}>
      <CircularProgress /><Typography sx={{ mt: 2 }}>Loading receipts…</Typography>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center">
              <MoveToInbox sx={{ mr: 1, color: 'secondary.main' }} />
              <Typography variant="h5" color="secondary.main">Posted Goods Receipts</Typography>
            </Box>
            <IconButton onClick={() => navigate('/payables')} title="Payables Home"
              sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
              <Home />
            </IconButton>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {receipts.length === 0 ? <Typography>No goods receipts posted yet.</Typography> : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Receipt No</strong></TableCell>
                  <TableCell><strong>Order No</strong></TableCell>
                  <TableCell><strong>Supplier</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>Location</strong></TableCell>
                  <TableCell align="center"><strong>Lines</strong></TableCell>
                  <TableCell align="right"><strong>Qty</strong></TableCell>
                  <TableCell align="right"><strong>Value ({sym()})</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {receipts.map(r => (
                  <TableRow key={r.receiptNo} hover sx={{ cursor: 'pointer' }} onClick={() => setView(r)}>
                    <TableCell><strong>{r.receiptNo}</strong></TableCell>
                    <TableCell>{r.orderNo}</TableCell>
                    <TableCell>{r.supplierCode}</TableCell>
                    <TableCell>{fmtD(r.receiptDate)}</TableCell>
                    <TableCell>
                      {r.locationCode
                        ? <Chip label={r.locationCode} size="small" variant="outlined" />
                        : <Typography variant="caption" color="text.disabled">—</Typography>}
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={r.lines?.length || 0} size="small" color="info" variant="outlined"
                        sx={{ fontWeight: 700, minWidth: 40 }} />
                    </TableCell>
                    <TableCell align="right">{qtyOf(r)}</TableCell>
                    <TableCell align="right">{fmt(valOf(r))}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Receipt view dialog */}
      <Dialog open={Boolean(view)} onClose={() => setView(null)} maxWidth="md" fullWidth>
        {view && (
          <>
            <DialogTitle component="div">
              <Box display="flex" alignItems="center" gap={1}>
                <MoveToInbox color="secondary" />
                <Box>
                  <Typography variant="h6" lineHeight={1}>{view.receiptNo}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Goods Receipt — from order {view.orderNo}
                  </Typography>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Box display="flex" gap={3} flexWrap="wrap" mb={1.5}>
                <Typography variant="body2"><strong>Supplier:</strong> {view.supplierCode}</Typography>
                <Typography variant="body2"><strong>Date:</strong> {fmtD(view.receiptDate)}</Typography>
                <Typography variant="body2"><strong>Location:</strong> {view.locationCode || '—'}</Typography>
              </Box>
              <Divider sx={{ mb: 1.5 }} />
              <Typography variant="subtitle2" color="secondary.main" gutterBottom>
                <Inventory sx={{ fontSize: 18, verticalAlign: 'middle', mr: 0.5 }} />
                Received Lines ({view.lines?.length || 0})
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Item</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Qty</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Unit Cost</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(view.lines || []).map((l, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{l.lineNo || idx + 1}</TableCell>
                      <TableCell>{l.itemNo}</TableCell>
                      <TableCell>{l.description}</TableCell>
                      <TableCell align="right">{l.qty}</TableCell>
                      <TableCell align="right">{fmt(l.unitCost)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt(l.amount)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={5} align="right" sx={{ fontWeight: 700 }}>Total Value</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{sym()} {fmt(valOf(view))}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </DialogContent>
            <DialogActions>
              <Button variant="outlined" onClick={() => setView(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default PurchaseReceiptList;
